import { useNavigate, useSearch } from '@tanstack/react-router';
import { CircleDot } from 'lucide-react';
import { Fragment, type ReactElement, useMemo, useState } from 'react';

import { AutomationRunFiles } from '@apps/web/components/automations/customer/automation_run_files';
import {
    AUTOMATION_RUN_STATUS_LABELS,
    automationRunStatusBadge,
    automationSkipTooltip,
    selectedAutomationRunStatuses,
} from '@apps/web/components/automations/customer/automation_run_status';
import {
    type AutomationRunLedgerRow,
    useAutomationRunLedger,
} from '@apps/web/components/automations/customer/use_automation_run_ledger';
import { Button } from '@apps/web/components/ui/button';
import { DataTable } from '@apps/web/components/ui/data_table';
import { DataTableToolbar } from '@apps/web/components/ui/data_table_toolbar';
import { StatusBadge } from '@apps/web/components/ui/status_badge';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import { formatWeekdayDateAndTime } from '@apps/web/lib/date_utils';
import { type ColumnDef, type ColumnFiltersState } from '@apps/web/lib/table';
import { cn } from '@apps/web/lib/utils';

const RUNS_PAGE_SIZE = 20;
const ROUTINE_DETAIL_PATH = '/routines/$routineId';
const ROUTINE_DETAIL_ROUTE_ID = '/_app/(main)/routines/$routineId';

export function AutomationRunsTable({
    automationId,
    failedCount,
    organizationId,
    runCount,
    timezone,
}: {
    readonly automationId: string;
    readonly failedCount: number;
    readonly organizationId: string;
    readonly runCount: number;
    readonly timezone: string;
}): ReactElement {
    const navigate = useNavigate({ from: ROUTINE_DETAIL_PATH });
    const { run: selectedRunId } = useSearch({
        from: ROUTINE_DETAIL_ROUTE_ID,
    });
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

    const selectedStatuses = useMemo(
        () =>
            selectedAutomationRunStatuses(
                columnFilters.find((filter) => filter.id === 'status')?.value,
            ),
        [columnFilters],
    );
    const automation = useMemo(
        () => ({ id: automationId, triggerType: 'schedule' as const }),
        [automationId],
    );
    const ledger = useAutomationRunLedger({
        organizationId,
        automation,
        selectedFacets: selectedStatuses,
        pageSize: RUNS_PAGE_SIZE,
    });

    const columns = useMemo<Array<ColumnDef<AutomationRunLedgerRow>>>(
        () => [
            {
                id: 'started',
                accessorFn: (row) => row.startedAt,
                header: 'Started',
                // A row ::before renders as an extra cell; use the first cell.
                meta: { cellClassName: 'relative' },
                cell: ({ row }) => (
                    <Fragment>
                        {row.original.id === selectedRunId ? (
                            <span
                                aria-hidden="true"
                                className="bg-foreground absolute inset-y-0 left-0 w-0.5"
                            />
                        ) : null}
                        <span className="tabular-nums">
                            {formatWeekdayDateAndTime(
                                row.original.startedAt,
                                timezone,
                            )}
                        </span>
                    </Fragment>
                ),
            },
            {
                id: 'status',
                accessorFn: (row) => row.status,
                header: 'Status',
                cell: ({ row }) => <RunStatusCell row={row.original} />,
            },
        ],
        [selectedRunId, timezone],
    );

    const facetOptions = useMemo(
        () =>
            AUTOMATION_RUN_STATUS_LABELS.filter((label) => label !== 'Skipped'),
        [],
    );

    return (
        <section
            aria-labelledby="automation-runs-heading"
            className="space-y-4"
        >
            <h2
                id="automation-runs-heading"
                className="text-foreground text-[15px] leading-[22px] font-semibold"
            >
                {failedCount > 0
                    ? `Runs · ${runCount} · ${failedCount} failed`
                    : `Runs · ${runCount}`}
            </h2>
            <DataTable
                columns={columns}
                data={ledger.rows}
                columnFilters={columnFilters}
                onColumnFiltersChange={setColumnFilters}
                manualFiltering={true}
                hidePagination={true}
                isLoading={ledger.isLoading}
                loadingLabel="Loading runs…"
                getRowClassName={(row) =>
                    cn(
                        row.isSkip && 'cursor-default',
                        row.id === selectedRunId && 'bg-muted',
                    )
                }
                onRowClick={(row) => {
                    if (row.isSkip) {
                        return;
                    }
                    void navigate({
                        search: (previous) => ({ ...previous, run: row.id }),
                    });
                }}
                renderExpandedRow={(row) =>
                    row.id === selectedRunId &&
                    !row.isSkip &&
                    row.attachmentCount > 0 ? (
                        <AutomationRunFiles
                            organizationId={organizationId}
                            runId={row.id}
                            timezone={timezone}
                        />
                    ) : null
                }
                renderToolbar={(table) => (
                    <DataTableToolbar
                        table={table}
                        hideGlobalSearch={true}
                        hideColumnsButton={true}
                        facetedFilters={[
                            {
                                columnId: 'status',
                                title: 'Status',
                                icon: CircleDot,
                                predefinedOptions: facetOptions,
                            },
                        ]}
                    />
                )}
                renderEmptyState={() =>
                    ledger.error ? (
                        <p
                            role="alert"
                            className="text-destructive py-6 text-center text-sm"
                        >
                            Couldn’t load these runs.
                        </p>
                    ) : (
                        <p className="text-muted-foreground py-6 text-center text-sm">
                            No runs yet.
                        </p>
                    )
                }
            />
            {ledger.hasMore ? (
                <div className="flex justify-center">
                    <Button
                        type="button"
                        variant="outline"
                        disabled={ledger.isLoadingMore}
                        onClick={ledger.loadMore}
                    >
                        Load more
                    </Button>
                </div>
            ) : null}
        </section>
    );
}

function RunStatusCell({
    row,
}: {
    readonly row: AutomationRunLedgerRow;
}): ReactElement {
    const badge = <StatusBadge status={automationRunStatusBadge(row.status)} />;
    const tooltip = row.isSkip
        ? automationSkipTooltip(row.suppressionReason)
        : null;
    if (tooltip === null) {
        return badge;
    }
    return (
        <Tooltip>
            <TooltipTrigger asChild={true}>
                <span className="inline-flex">{badge}</span>
            </TooltipTrigger>
            <TooltipContent>{tooltip}</TooltipContent>
        </Tooltip>
    );
}
